
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { intensityMap } from '../const/data_index';
import { fetchData } from "../func/api";
import { POWERPLANT_REPORT_STANDARD_INTENSITY, DAM_REPORT_STANDARD_INTENSITY } from '../const/data_index';

// プラグインを有効化
dayjs.extend(utc);
dayjs.extend(timezone);

// 日時フォーマット変換
export const formatteDateTimeZone = (date) => {
  return dayjs.utc(date).tz("Asia/Tokyo").format("YYYY/MM/DD HH:mm");
}

export const formatteDateTimeZoneJP = (date) => {
  return dayjs.utc(date).tz("Asia/Tokyo").format("YYYY年MM月DD日 HH:mm");
}

export const formatteDateText = (date) => {
  return dayjs.utc(date).format("YYYY/MM/DD HH:mm");
}

// 日時フォーマット変換
export const formatteDateTimeZoneNoFormat = (date) => {
  return dayjs.utc(date).tz("Asia/Tokyo", true);
}

export const formatteDate = (date) => {
  return dayjs(date).subtract(9, "hour").format("YYYY/MM/DD HH:mm");
}

// 電話番号フォーマット変換
export const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, ''); // 数字だけ抽出

  // 携帯番号
  if (/^0[789]0\d{8}$/.test(digits)) {
    return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  }

  // 固定電話（市外局番3桁＋市内局番3桁＋加入者番号4桁）
  if (/^0\d{9}$/.test(digits)) {
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }

  // 固定電話（市外局番4桁＋市内局番2桁＋加入者番号4桁）
  if (/^0\d{10}$/.test(digits)) {
    return digits.replace(/(\d{4})(\d{2})(\d{4})/, '$1-$2-$3');
  }

  return digits; // それ以外はそのまま
};

// 小文字数字を大文字に変換するヘルパー関数
export const normalizeIntensity = (value) => {
  if (!value) return value;

  // 全角数字の変換マップ
  const numberMap = {
    '1': '１', '2': '２', '3': '３', '4': '４',
    '5': '５', '6': '６', '7': '７', '8': '８', '9': '９'
  };

  let normalized = value;

  // 半角数字を全角数字に変換
  Object.keys(numberMap).forEach(half => {
    normalized = normalized.replace(new RegExp(half, 'g'), numberMap[half]);
  });

  return normalized;
};

// 震度の優先順位を定義（数値が大きいほど強い震度）
export const getIntensityPriority = (intensity) => {
  return intensityMap[intensity] || 0;
};

// 様式ダ-3の点検結果更新関数
export const handleUpdateCheckResultDam3 = (setCheckResult, updatedRow) => {
  setCheckResult(prevCheckResult => {
    return prevCheckResult.map(row => {
      // 同じレコード（earthquake_intensity_uuid）の場合
      if (row.earthquake_intensity_uuid === updatedRow.earthquake_intensity_uuid) {
        // 様式ダ-3固有のフィールドのみ更新、共有フィールドは元の値を保持
        return {
          ...row,
          intensity: updatedRow.intensity,
          inspections_name: updatedRow.inspections_name,
        };
      }
      
      return row;
    });
  });
};

// 様式1の点検結果更新関数
export const handleUpdateCheckResult = (setCheckResult, updatedRow, facilityDams) => {
  setCheckResult(prevCheckResult => {
    return prevCheckResult.map(row => {

      // 1. ダムかどうか、および施設UUIDが一致するか判定
      const isTargetDam = facilityDams.includes(updatedRow.facility_uuid) && 
                          row.facility_uuid === updatedRow.facility_uuid;

      if (isTargetDam) {
        // A. situationなどは、施設が同じであれば全レコード更新
        const newValues = {
          situation: updatedRow.situation,
          anomaly_facility_name: updatedRow.anomaly_facility_name,
          has_anomary: updatedRow.has_anomary,
          has_impact: updatedRow.has_impact,
          has_media_response: updatedRow.has_media_response,
        };

        // B. intensityだけは、UUIDが完全に一致（今画面で見ているレコード）する場合のみ更新
        if (row.earthquake_intensity_uuid === updatedRow.earthquake_intensity_uuid) {
          return {
            ...row,
            ...newValues,
            intensity: updatedRow.intensity,
          };
        }

        // C. 同じ施設だが、表示用ではない他2つのレコード
        return {
          ...row,
          ...newValues, // 状況(situation)などは更新するが、震度はそのまま
        };
      }

      // ダム以外、または施設が異なる場合は、従来のロジック（UUID完全一致のみ更新）
      if (row.earthquake_intensity_uuid === updatedRow.earthquake_intensity_uuid) {
        return {
          ...updatedRow,
          inspections_name: row.inspections_name,
          earthquake_intensity_uuid: row.earthquake_intensity_uuid,
          note: row.note,
        };
      }
      return row;
    });
  });
};

// 地震発生日時フォーマット変換
export const formatJSTDate = (isoString) => {
  if (!isoString) return '-'; // undefined/null チェック

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '-'; // 無効な日付チェック

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${year}年${month}月${day}日${hours}時${minutes}分`;
};

// 対応状況日時フォーマット変換
export const formatReportDate = (isoString) => {
  if (!isoString) return '-';

  const date = new Date(isoString);

  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 0始まりなので +1
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0'); // 2桁に補正

  return `${year}年${month}月${day}日 ${hours}:${minutes}`;
};

export const convertTaskAssignmentsToApiFormat = (taskAssignments, earthquake) => {
  const assignmentsMap = {};

  // taskAssignmentsをループして変換
  Object.entries(taskAssignments).forEach(([key, userUuid]) => {
    // ユーザーが選択されていない場合は、ユーザーIDをNoneでAPIに渡す
    if (userUuid === '') {
      userUuid = null
    }
    // keyの形式: "dam-work-uuid-1" または "powerpoint-work-uuid-2"
    const parts = key.split('-');
    const category = parts[0]; // "dam" or "powerpoint"
    const userSlot = parts[parts.length - 1]; // "1" or "2"
    const workUuid = parts.slice(1, -1).join('-'); // work-uuid部分

    const mapKey = `${category}-${workUuid}`;

    if (!assignmentsMap[mapKey]) {
      assignmentsMap[mapKey] = {
        report_uuid: earthquake.report_uuid,
        work_uuid: workUuid,
        user_uuid01: null,
        user_uuid02: null
      };
    }

    // 担当者を設定
    if (userSlot === '1') {
      assignmentsMap[mapKey].user_uuid01 = userUuid;
    } else if (userSlot === '2') {
      assignmentsMap[mapKey].user_uuid02 = userUuid;
    }
  });

  return Object.values(assignmentsMap);
};

export const shouldValidateSituation = (facilityUuid, intensity, facilityDams) => {
    const isDam = facilityDams.includes(facilityUuid);
    const intensityPriority = intensityMap[normalizeIntensity(intensity)] || 0;
    const damThreshold = intensityMap[DAM_REPORT_STANDARD_INTENSITY] || 0;
    const powerthreshold = intensityMap[POWERPLANT_REPORT_STANDARD_INTENSITY] || 0;
    
    // ダムかつ DAM_REPORT_STANDARD_INTENSITY 以上、または POWERPLANT_REPORT_STANDARD_INTENSITY 以上
    return (isDam && intensityPriority >= damThreshold) || 
           intensityPriority >= powerthreshold;
};

// 背景色を決定する関数
export const getCellBgColor = (hasValue, isRowDisabled, isCellLogicalDisabled) => {
  if (isRowDisabled || isCellLogicalDisabled) return '#eeeeee'; // 無効時はグレー
  if (hasValue) return '#e8f5e9'; // 入力済みなら緑（is_completedと同じ）
  return '#ffffff'; // 未入力なら透明
};